// src/hooks/apollo-dutchie-mutations.js
import { useMemo } from '@wordpress/element';
import { useMutation, useApolloClient } from '@apollo/client';
import { getErrorMsgsToSet, hasCommonErrCode, sentryCapture } from '../errorHandlers';
import * as storage from '../browserStorage';
import { storageItemName } from '../config';

// Import mutation documents
import ADD_ITEM_TO_CHECKOUT from '../api/queries/AddItemToCheckout.gql';
import CREATE_CHECKOUT from '../api/queries/CreateCheckout.gql';
import REMOVE_ITEM_FROM_CHECKOUT from '../api/queries/RemoveItemFromCheckout.gql';
import UPDATE_CHECKOUT from '../api/queries/UpdateCheckout.gql';
import UPDATE_ITEM_QUANTITY from '../api/queries/UpdateItemQuantity.gql';
import FETCH_CART_DETAILS from '../api/queries/FetchCartDetails.gql';

export const useAddItem = (
    retailerId,
    checkoutId,
    setSidebarToggle,
    setErrors = null,
    handleTracking = null
) => {
    const client = useApolloClient();

    const [addItemMutation, { loading, error }] = useMutation(ADD_ITEM_TO_CHECKOUT, {
        update: (cache, { data }) => {
            if (!data?.addItem) return;

            // Update the cart query in cache
            cache.writeQuery({
                query: FETCH_CART_DETAILS,
                variables: { checkoutId, retailerId },
                data: { checkout: data.addItem }
            });
        },
        onCompleted: (data) => {
            setSidebarToggle(true);
            setErrors?.([]);
            handleTracking?.();
        },
        onError: (error) => {
            console.error('Add item error:', error);
            if (error?.graphQLErrors) {
                const errMsgs = getErrorMsgsToSet(error.graphQLErrors);
                setErrors?.(errMsgs);
                
                if (!hasCommonErrCode(error.graphQLErrors)) {
                    sentryCapture(error);
                }
            }
        }
    });

    // Return the mutate function that matches the expected API
    return {
        mutate: (inputVariables) => {            
            // Apollo mutations expect variables to be passed directly
            // not wrapped in a 'variables' object
            return addItemMutation({
                variables: {
                    retailerId,
                    checkoutId,
                    productId: inputVariables.productId,
                    quantity: inputVariables.quantity,
                    option: inputVariables.option,
                    // Optional fields
                    ...(inputVariables.specialId && { specialId: inputVariables.specialId }),
                    ...(inputVariables.basketOption && { basketOption: inputVariables.basketOption })
                }
            });
        },
        loading,
        error,
        isPending: loading  // For compatibility with existing code
    };
};

// Create Cart Mutation
export const useCreateCart = (retailerId) => {
    const client = useApolloClient();

    const [createCheckoutMutation, { loading, error, data }] = useMutation(CREATE_CHECKOUT, {
        onCompleted: (data) => {
            const checkout = data.createCheckout;
            
            // Store in localStorage
            storage.setItem(
                storageItemName,
                JSON.stringify({
                    checkoutId: checkout.id,
                    retailerId: retailerId,
                    createdAt: Date.now(),
                })
            );

            // Write to cache
            client.cache.writeQuery({
                query: FETCH_CART_DETAILS,
                variables: { checkoutId: checkout.id, retailerId },
                data: { checkout }
            });
        },
        onError: (error) => {
            console.error('Create cart error:', error);
            if (!hasCommonErrCode(error.graphQLErrors || [])) {
                sentryCapture(error);
            }
        }
    });

    return {
        mutate: (inputVariables) => {            
            // Handle both old format (with nested variables) and new format
            const vars = inputVariables.variables || inputVariables;
            
            return createCheckoutMutation({
                variables: {
                    retailerId: vars.retailerId,
                    orderType: vars.orderType,
                    pricingType: vars.pricingType
                }
            });
        },
        loading,
        error,
        data,
        isPending: loading
    };
};

// Remove Item Mutation with Optimistic Response
export const useRemoveItem = (
    retailerId,
    setErrors,
    handleTracking,
    surfside,
    analytics
) => {
    const client = useApolloClient();

    const [removeItemMutation, { loading, error }] = useMutation(REMOVE_ITEM_FROM_CHECKOUT, {
        update: (cache, { data }) => {
            if (!data?.removeItem) return;
            // Apollo will automatically update the cache with the returned data
        },
        onError: (error) => {
            // Only log to Sentry if it's NOT a missing variable error
            const isMissingVariableError = error?.graphQLErrors?.some(err => 
                err.message.includes('was not provided')
            );
            
            if (!isMissingVariableError) {
                if (error?.graphQLErrors) {
                    const errMsgs = getErrorMsgsToSet(error.graphQLErrors);
                    setErrors?.(errMsgs);
                    
                    if (!hasCommonErrCode(error.graphQLErrors)) {
                        sentryCapture(error);
                    }
                } else {
                    sentryCapture(error);
                }
            }
            // Silently ignore missing variable errors - they're expected during initialization
        }
    });

    return {
        mutate: async (variables) => {
            const { item, currentVariant, checkoutId, itemId } = variables;
            
            // Only warn, don't block
            if (!checkoutId || !retailerId || (!itemId && !item?.id)) {
                console.debug('useRemoveItem: Called with incomplete data, will retry when ready');
            }

            try {
                const result = await removeItemMutation({
                    variables: {
                        checkoutId: checkoutId || '',
                        retailerId: retailerId || '',
                        itemId: itemId || item?.id || ''
                    }
                });

                // Call tracking after successful mutation
                if (result.data?.removeItem && handleTracking) {
                    handleTracking(surfside, analytics, item, currentVariant);
                }

                return result;
            } catch (error) {
                // Silently catch missing variable errors during initialization
                const isMissingVariableError = error?.graphQLErrors?.some(err => 
                    err.message.includes('was not provided')
                );
                
                if (!isMissingVariableError) {
                    throw error; // Re-throw real errors
                }
                // Return resolved promise for missing variable errors
                console.debug('useRemoveItem: Ignoring initialization error');
                return Promise.resolve();
            }
        },
        loading,
        error
    };
};

// Update Checkout Mutation
export const useUpdateCheckout = (retailerId) => {
    const client = useApolloClient();
    
    // Memoize the mutation to prevent recreation
    const updateCheckoutMutation = useMemo(() => {
        return (variables) => {
            if (!retailerId) {
                console.warn('useUpdateCheckout called without retailerId');
                return Promise.reject(new Error('No retailerId provided'));
            }
            
            return client.mutate({
                mutation: UPDATE_CHECKOUT,
                variables,
                update: (cache, { data }) => {
                    if (!data?.updateCheckout) return;

                    cache.writeQuery({
                        query: FETCH_CART_DETAILS,
                        variables: { 
                            checkoutId: data.updateCheckout.id, 
                            retailerId 
                        },
                        data: { checkout: data.updateCheckout }
                    });
                },
                onError: (error) => {
                    if (!hasCommonErrCode(error.graphQLErrors || [])) {
                        sentryCapture(error);
                    }
                }
            });
        };
    }, [retailerId, client]);
    
    // Return a stable object with mutate function
    return useMemo(() => ({
        mutate: updateCheckoutMutation,
        // Add these for compatibility if needed elsewhere
        isPending: false,
        isLoading: false
    }), [updateCheckoutMutation]);
};

// Update Quantity Mutation with Optimistic Response
export const useUpdateQuantity = (
    retailerId,
    checkoutId,
    setSidebarToggle = null,
    setErrors = null,
    handleTracking = null
) => {
    const client = useApolloClient();
    
    // Don't skip - let the mutation be available even without IDs
    const [updateQuantity, { loading, error }] = useMutation(UPDATE_ITEM_QUANTITY, {
        onCompleted: () => {
            setSidebarToggle?.(true);
            setErrors?.([]);
            handleTracking?.();
        },
        onError: (error) => {
            // Only log to Sentry if it's NOT a missing variable error
            const isMissingVariableError = error?.graphQLErrors?.some(err => 
                err.message.includes('was not provided')
            );
            
            if (!isMissingVariableError) {
                if (setErrors) {
                    const errMsgs = getErrorMsgsToSet(error.graphQLErrors || []);
                    setErrors(errMsgs);
                }
                
                if (!hasCommonErrCode(error.graphQLErrors || [])) {
                    sentryCapture(error);
                }
            }
            // Silently ignore missing variable errors - they're expected during initialization
        }
    });

    return { 
        mutate: (inputVariables) => {
            const vars = inputVariables.variables || inputVariables;
            
            // Only warn, don't block
            if (!checkoutId || !retailerId || !vars.itemId || vars.quantity === undefined) {
                console.debug('useUpdateQuantity: Called with incomplete data, will retry when ready');
                // Still attempt the mutation - let GraphQL handle it
            }

            return updateQuantity({
                variables: {
                    retailerId: retailerId || '',
                    checkoutId: checkoutId || '',
                    itemId: vars.itemId || '',
                    quantity: vars.quantity ?? 0
                }
            }).catch(error => {
                // Silently catch missing variable errors during initialization
                const isMissingVariableError = error?.graphQLErrors?.some(err => 
                    err.message.includes('was not provided')
                );
                
                if (!isMissingVariableError) {
                    throw error; // Re-throw real errors
                }
                // Return resolved promise for missing variable errors
                return Promise.resolve();
            });
        },
        loading,
        error,
        isPending: loading
    };
};
